import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  TechnologyApplication,
  TechnologyCatalogItem,
  listTechnologyCatalogItems,
  updateTechnologyApplication,
} from '../../services/technologyApi';
import { technologyOptionLabel, TECHNOLOGY_CATALOG_CATEGORY_OPTIONS } from './technologyFormOptions';

type TechnologyApplicationInventoryModalProps = {
  open: boolean;
  application: TechnologyApplication;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

export function TechnologyApplicationInventoryModal({
  open,
  application,
  canWrite,
  onClose,
  onSaved,
}: TechnologyApplicationInventoryModalProps) {
  const [catalogItems, setCatalogItems] = useState<TechnologyCatalogItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedIds((application.catalogItems ?? []).map((item) => item.id));
    setLoading(true);
    void listTechnologyCatalogItems({ page: 1, pageSize: 200 })
      .then((result) => setCatalogItems(result.items))
      .catch(() => {
        setCatalogItems([]);
        toast.error('Falha ao carregar o inventário.');
      })
      .finally(() => setLoading(false));
  }, [open, application]);

  function toggleCatalogItem(catalogItemId: string): void {
    setSelectedIds((current) =>
      current.includes(catalogItemId)
        ? current.filter((id) => id !== catalogItemId)
        : [...current, catalogItemId],
    );
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    setSaving(true);
    try {
      await updateTechnologyApplication(application.id, {
        catalogItemIds: selectedIds,
        name: application.name,
        version: application.version ?? null,
        repositoryUrl: application.repositoryUrl ?? null,
        pipelineUrl: application.pipelineUrl ?? null,
        azureDevOpsOrganization: application.azureDevOpsOrganization ?? null,
        azureDevOpsProject: application.azureDevOpsProject ?? null,
        azureDevOpsRepository: application.azureDevOpsRepository ?? null,
        azureDevOpsPatSecretId: application.azureDevOpsPatSecretId ?? null,
        status: application.status,
        ownerUserId: application.ownerUserId ?? null,
        notes: application.notes ?? null,
      });
      toast.success('Composição do inventário atualizada.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar o inventário.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DraggableModalPanel
      open={open}
      title="Inventário"
      subtitle={`Composição tecnológica de ${application.name}`}
      onClose={onClose}
      className="w-full max-w-xl"
      footer={
        canWrite ? (
          <FormActionBar onCancel={onClose} cancelLabel="Cancelar" className="erp-btn-actions">
            <button
              type="submit"
              form="technology-application-inventory-form"
              disabled={saving || loading}
              className="erp-btn-primary"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </FormActionBar>
        ) : (
          <FormActionBar className="erp-btn-actions">
            <button type="button" className="erp-btn-secondary" onClick={onClose}>
              Fechar
            </button>
          </FormActionBar>
        )
      }
    >
      <form
        id="technology-application-inventory-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="erp-form space-y-3 p-1"
      >
        <p className="text-sm text-secondary-600">
          Selecione as tecnologias padrão que compõem esta aplicação. A hospedagem concreta de cada
          peça é configurada em seguida, por ambiente.
        </p>
        {loading ? (
          <p className="text-secondary-500">Carregando...</p>
        ) : (
          <label className="erp-label block">
            <FieldLabel
              description="Itens do inventário tecnológico vinculados a esta aplicação."
              example="C#, PostgreSQL 17, .NET 8"
            >
              Componentes
            </FieldLabel>
            <div className="erp-input mt-1 max-h-72 space-y-1 overflow-y-auto p-2">
              {catalogItems.length === 0 ? (
                <p className="text-sm text-secondary-500">Nenhum item no inventário.</p>
              ) : (
                catalogItems.map((item) => (
                  <label key={item.id} className="flex items-center gap-2 text-sm font-normal">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleCatalogItem(item.id)}
                      disabled={!canWrite}
                    />
                    <span>
                      {item.name}
                      {item.version ? ` (${item.version})` : ''}
                      <span className="text-secondary-500">
                        {' '}
                        · {technologyOptionLabel(TECHNOLOGY_CATALOG_CATEGORY_OPTIONS, item.category)}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </label>
        )}
      </form>
    </DraggableModalPanel>
  );
}
